// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import clsx from 'clsx';
import i18next from 'i18next';
import {
    ComboBox,
    IComboBoxStyles,
    ITextFieldStyles,
    Spinner,
    SpinnerSize,
    TextField,
    TooltipHost,
    IDropdownOption,
    IComboBoxOption
} from 'office-ui-fabric-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import { AnnounceMessage } from 'react-screen-reader-announce';

import { validateSerialNumber, validatePostalCode } from './../../helpers/validation';
import { CountryType } from './../../store/countries/countriesReducer';
import { putSelectedCountryData } from './../../store/countries';
import { getClaimDetails, getDeviceInfoData, GET_DEVICE_INFO_DATA } from './../../store/deviceInfo/deviceInfo';

import { actionStatusReset, LoadingState } from './../../store/actionStatus';
import { userAgentTierSelector } from './../../store/user/selector';
import { resetStoreOnSearch } from './../../store/action';

import { countriesSelector, statusSelector } from './selector';

import tooltipIcon from './../../assets/tooltip-copy-3@2x.png';

import Styles from './serviceRequestAndDeviceLookup.module.scss';
import { logPageAction } from 'src/lib/ApplicationInsights';
import { getFeatureFlagsData } from './../../store/config/configReducer';

export interface DeviceInputPropType {
    onError?: (errorMessage: string) => void;
}

const dropdownStyles: Partial<IComboBoxStyles> = {
    container: {
        minWidth: 200,
        marginLeft: 22,
    },
    optionsContainerWrapper: {
        maxHeight: 350,
    },
    label: {
        marginLeft: 22,
    },
};

const textFieldStyles: Partial<ITextFieldStyles> = {
    root: {
        marginLeft: 22,
        width: 160,
    },
};

let translate: i18next.TFunction;
export default function DeviceInputs(props: DeviceInputPropType) {
    const dispatch = useDispatch();
    const [countryInfo, setCountryInfo] = React.useState({ countryCode: 'USA', countryName: 'United States of America', countryShortCode: 'US' });
    const [serialNumber, setSerialNumber] = React.useState('');
    const [zipCode, setZipCode] = React.useState('');

    translate = useTranslation().t;

    const countriesList = useSelector(state => countriesSelector(state));
    const serialNumberLoadingState = useSelector(state => statusSelector(state)(GET_DEVICE_INFO_DATA));
    const agentTier = useSelector(state => userAgentTierSelector(state));

    const isDisabled = serialNumberLoadingState.status === LoadingState.started;
    const loadingComplete = serialNumberLoadingState.status === LoadingState.success || serialNumberLoadingState.status === LoadingState.rejected;

    const buttonDisabled = isDisabled || !countryInfo.countryCode || !serialNumber;

    const countryOptions: IDropdownOption[] = countriesList.map((option: CountryType): IComboBoxOption => ({
        key: option.ISOCode,
        text: translate(option.ISOName),
        data: option.ISOCountryShortCode,
    }));
    const [options, setOptions] = useState(countryOptions);

    function onClick(event: any) {
        const { countryCode } = countryInfo;
        logPageAction('Continue to device details page: Select button clicked');

        event.preventDefault();
        dispatch(actionStatusReset({ actionName: GET_DEVICE_INFO_DATA }));
        if (!validateSn()) {
            if (props.onError) {
                props.onError(translate('please_provide_valid_serial_number'));
            }
            return;
        }

        if (!validateZipCode()) {
            if (props.onError) {
                props.onError(translate('please_provide_valid_postal_code'));
            }
            return;
        }
        if (props.onError) {
            props.onError('');
        }
        dispatch(resetStoreOnSearch());
        dispatch(
            getDeviceInfoData({
                countryCode,
                serialNumber,
                zipCode,
                agentTier,
            })
        );
        dispatch(
            getClaimDetails({
                serialNumber,
            })
        );
        dispatch(putSelectedCountryData(countryInfo));
        dispatch(getFeatureFlagsData());
    }

    function onCountryChange(
        event: any,
        selectedOption?: IDropdownOption
    ) {
        if (selectedOption) {
            logPageAction('Country dropdown item selected', { itemSelected: selectedOption.text });
            const { key, text, data } = selectedOption;
            setCountryInfo({ countryCode: key.toString(), countryName: text.toString(), countryShortCode: data });
        }
    }
    function onPendingValueCountryChanged(selectedOption?: IComboBoxOption, pendingIndex?: number, pendingValue?: string) {
        if ((typeof (pendingValue) === 'undefined' && typeof (selectedOption) === 'undefined') || (typeof (selectedOption) !== 'undefined')) {
            return;
        }

        if (typeof (pendingValue) !== 'undefined') {
            const filteredCountryOptions = countryOptions.filter((countryObj: any) => (countryObj.text).toLowerCase().startsWith(pendingValue.toLowerCase()));
            setOptions(filteredCountryOptions);
        }
    }

    function onSerialNumberChange(event: any) {
        if (props.onError) {
            props.onError('');
        }
        dispatch(actionStatusReset({ actionName: GET_DEVICE_INFO_DATA }));
        setSerialNumber(event.target.value && event.target.value.trim());
    }

    function onZipCodeChange(event: any) {
        setZipCode(event.target.value && event.target.value.trim());
    }

    function validateSn(): boolean {
        if (!serialNumber || !validateSerialNumber(serialNumber)) {
            return false;
        }
        return true;
    }

    function validateZipCode(): boolean {
        const { countryCode } = countryInfo;
        if (zipCode && !validatePostalCode(countryCode, zipCode)) {
            return false;
        }
        return true;
    }
    const buttonClassName = clsx(
        Styles.button,
        Styles.commonFocus,
        { [Styles.disabled]: buttonDisabled }
    );

    if (isDisabled) {
        AnnounceMessage(translate('loading'));
    } else if (loadingComplete) {
        // Only say loading complete on success/failure, else React will go in infinite loop during change user tier
        AnnounceMessage(translate('loading_complete'));
    }

    return (
        <form className={Styles.inputsSection}>
            <ComboBox
                ariaLabel={translate('country_region')}
                autoComplete="off"
                disabled={isDisabled}
                label={translate('country_region')}
                onChange={onCountryChange}
                options={options}
                selectedKey={countryInfo.countryCode}
                styles={dropdownStyles}
                allowFreeform={true}
                onPendingValueChanged={onPendingValueCountryChanged}
                openOnKeyboardFocus={true}
                useComboBoxAsMenuWidth={true}
            />
            <TextField
                autoComplete="on"
                name="ZipCode"
                aria-labelledby="zip-code-label"
                disabled={isDisabled}
                label={translate('zip_code')}
                onChange={onZipCodeChange}
                onRenderLabel={RenderZipCodeLabel}
                styles={textFieldStyles}
            />
            <TextField
                autoComplete="on"
                name="SerialNumber"
                ariaLabel={translate('serial_number')}
                disabled={isDisabled}
                label={translate('serial_number')}
                required={true}
                onChange={onSerialNumberChange}
                styles={textFieldStyles}
            />
            <button
                name="button"
                type="submit"
                aria-label={translate('select')}
                className={buttonClassName}
                disabled={buttonDisabled}
                onClick={onClick}
            >
                {translate('select')}
            </button>
            {isDisabled && <Spinner
                className={Styles.spinnerStyle}
                size={SpinnerSize.medium}
            />}
        </form>
    );
}

function RenderZipCodeLabel(props: any): JSX.Element {
    return (
        <span id="zip-code-label"
            aria-label={props.label}
            className={Styles.label}>
            {props.label}
            <TooltipHost
                aria-label={translate(
                    'zip_code_is_only_required_for_service_order_creation'
                )}
                calloutProps={{ gapSpace: 0 }}
                content={translate(
                    'zip_code_is_only_required_for_service_order_creation'
                )}
                id="zip_code_label"
            >
                <img
                    alt={translate('zip_code_is_only_required_for_service_order_creation')}
                    aria-label={translate('zip_code_is_only_required_for_service_order_creation')}
                    className={Styles.faq}
                    src={tooltipIcon}
                    tabIndex={0}
                    aria-describedby="zip_code_label"
                />
            </TooltipHost>
        </span>
    );
}
